import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "../../lib/api";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ slug: "", title: "", excerpt: "" });

  const load = () => api.get("/blog", { params: { published_only: false } }).then(r => setPosts(r.data || []));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.slug || !form.title) { toast.error("Slug and title required"); return; }
    try {
      const { data } = await api.post("/blog", { ...form, blocks: [], published: true });
      toast.success("Post created");
      setCreateOpen(false);
      setForm({ slug: "", title: "", excerpt: "" });
      window.location.href = `/admin/blog/${data.id}`;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/blog/${p.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-5 sm:p-8 lg:p-10" data-testid="admin-blog">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="label-eyebrow mb-3">Content</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[#1a1a1a]">Blog posts</h1>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary-burgundy" data-testid="new-post-btn"><Plus size={14} /> New post</button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-serif-display text-2xl">Create blog post</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="post-title-input" /></div>
              <div><Label>URL slug</Label><Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} data-testid="post-slug-input" /></div>
              <div><Label>Excerpt (short summary)</Label><Input value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <button className="btn-outline-burgundy" onClick={() => setCreateOpen(false)}>Cancel</button>
              <button className="btn-primary-burgundy" onClick={create} data-testid="post-create-submit">Create & edit</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {posts.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-neutral-300 text-neutral-500" data-testid="no-posts">No blog posts yet.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map(p => (
            <div key={p.id} className="bg-white border border-neutral-200 overflow-hidden" data-testid={`post-card-${p.slug}`}>
              {p.cover_image ? (
                <img src={fileUrl(p.cover_image)} alt={p.title} className="w-full h-40 object-cover" />
              ) : (
                <div className="w-full h-40 bg-neutral-100 flex items-center justify-center text-neutral-300 text-xs">No cover image</div>
              )}
              <div className="p-5">
                <p className="text-xs text-neutral-500 mb-2">{new Date(p.created_at).toLocaleDateString()} · {p.published ? "Published" : "Draft"}</p>
                <h3 className="font-serif-display text-xl mb-2 text-[#1a1a1a]">{p.title}</h3>
                <p className="text-sm text-neutral-600 line-clamp-2 mb-4">{p.excerpt}</p>
                <div className="flex justify-between items-center">
                  <Link to={`/admin/blog/${p.id}`} className="text-sm text-[#6B1F2A] inline-flex items-center gap-1 hover:underline" data-testid={`edit-post-${p.slug}`}><Pencil size={12} /> Edit</Link>
                  <button onClick={() => remove(p)} className="text-sm text-neutral-400 hover:text-red-600 inline-flex items-center gap-1" data-testid={`delete-post-${p.slug}`}><Trash2 size={12} /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
